import ScreenLayout from '@/components/layout/ScreenLayout'
import Skeleton from '@/components/ui/Skeleton'
import TitleRow from '@/components/ui/TitleRow'
import { cn } from '@/lib/utils'
import {
  CHECKOUT_COLUMNS,
  OPTIONS_COLUMN,
  OPTIONS_PAIR,
  OPTION_GROUP,
} from '@/screens/account/components/checkout/checkoutLayout'
import SubscriptionSteps from '@/screens/account/components/SubscriptionSteps'

/** A bar centred in one line of `text` — the loaded line's own box, so nothing grows. */
function Line({ text, className }: { text: string; className: string }) {
  return (
    <div className={cn('flex h-lh items-center', text)}>
      <Skeleton className={className} />
    </div>
  )
}

/** A radio row's box (`OptionRow`): `py-2`, a 14/600 title over a 12 description. */
function OptionRowSkeleton() {
  return (
    <div className="flex items-center gap-3 border-b border-border-subtle py-2 last:border-b-0">
      <Skeleton className="size-5 shrink-0 rounded-full" />
      <div className="min-w-0 flex-1">
        <Line text="text-h-s" className="h-3 w-24" />
        <Line text="text-body-xs" className="h-3 w-40" />
      </div>
    </div>
  )
}

function OptionGroupSkeleton({ rows }: { rows: number }) {
  return (
    <>
      <Line text="text-body-xs" className="h-3 w-20" />
      <div className="mt-1">
        {Array.from({ length: rows }, (_, row) => (
          <OptionRowSkeleton key={row} />
        ))}
      </div>
    </>
  )
}

/**
 * `/subscription/checkout` before the catalogue and the member's plan load —
 * only on a cold link, since `/subscription` leaves both cached. The loaded
 * page's blocks with its own grid (`checkoutLayout.ts`): the plan card, "How
 * often" in Guru's shape (three periods and the AI-signals pair — the tallest
 * the catalogue draws), "How you pay" at its three gateways, the code box,
 * the summary at its one plan row. Every block matched the loaded Guru page
 * at 1440 and 375. The title row and the steps are static, so they are the
 * real ones.
 */
export default function CheckoutPending() {
  return (
    <ScreenLayout header={<TitleRow title="Checkout" back="/subscription" />} fullWidth pending>
      <SubscriptionSteps current="pay" />
      <div className={CHECKOUT_COLUMNS}>
        <div className={OPTIONS_COLUMN}>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 bg-card px-5 py-4">
            <Skeleton className="size-11 shrink-0 rounded-full" />
            <div className="min-w-0 flex-1 basis-48">
              <Line text="text-h-section" className="h-5 w-24" />
              <Line text="text-body-xs" className="h-3 w-3/5" />
              <Line text="text-body-xs" className="h-3 w-2/5" />
            </div>
            <Skeleton className="h-5 w-20" />
          </div>
          <div className={OPTIONS_PAIR}>
            <div className={OPTION_GROUP}>
              <OptionGroupSkeleton rows={3} />
              <div className="mt-4 border-t border-border-subtle pt-4">
                <OptionGroupSkeleton rows={2} />
              </div>
            </div>
            <div className={OPTION_GROUP}>
              <OptionGroupSkeleton rows={3} />
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-2 bg-card p-4">
            <Line text="text-body-xs" className="h-3 w-20" />
            <Skeleton className="h-11 w-full" />
          </div>
        </div>
        <div className="min-w-0 bg-card px-4 pt-3 pb-2">
          <Line text="text-body-xs" className="h-3 w-24" />
          <div className="flex items-center justify-between gap-3 py-2">
            <Line text="text-body-m" className="h-3 w-32" />
            <Line text="text-h-s" className="h-3 w-16" />
          </div>
          <div className="flex items-center justify-between gap-3 border-t border-border py-3">
            <Line text="text-h-s" className="h-3 w-20" />
            <Line text="text-h-xl" className="h-6 w-24" />
          </div>
          <Skeleton className="h-12 w-full @md:w-32" />
          <div className="mt-2">
            <Line text="text-body-xs" className="h-3 w-48" />
          </div>
        </div>
      </div>
    </ScreenLayout>
  )
}
